import { useCallback, useState, useTransition } from "react";

/**
 * The refresh seam every editable txn panel shares.
 *
 * A `fetchKey` bump alone does NOT refetch. Relay evaluates the fetch policy on
 * every re-evaluation, and `store-or-network` sets `shouldFetch = !hasFullQuery`
 * — after a mutation the store still holds a COMPLETE (just stale) copy of the
 * query, so no request is sent and the panel keeps rendering the pre-edit rows.
 * That is why every bulk action toasted success while the table sat unchanged.
 *
 * `network-only` forces the round-trip. On its own it would also suspend and drop
 * the whole table to its Suspense fallback (losing scroll position and the sense
 * of continuity), so the bump runs inside a transition: React keeps the current
 * rows on screen and reports `refreshing`, which the panel renders as an in-place
 * indicator over the live table.
 *
 * The two states want different policies, so the policy follows the key. Opening
 * the panel (key 0) paints from the store immediately and revalidates behind it;
 * every refresh after that goes straight to the network, because the whole point
 * of the bump is that the store is the thing we no longer trust.
 */
export function useTxnPanelRefresh() {
  const [reloadKey, setReloadKey] = useState(0);
  const [refreshing, startTransition] = useTransition();
  const refresh = useCallback(
    () => startTransition(() => setReloadKey((key) => key + 1)),
    []
  );

  return {
    refreshing,
    refresh,
    /** Spread into useLazyLoadQuery's options argument. */
    queryOptions: {
      fetchKey: reloadKey,
      fetchPolicy: (reloadKey === 0 ? "store-and-network" : "network-only") as
        | "store-and-network"
        | "network-only",
    },
  };
}
